import { useCallback, useMemo } from 'react';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import type { IPageContainerRouteErrorState } from '../../../components/layout/page-container/page-container-error-handler';
import { useNotFound } from '../../../hooks/useNotFound';
import {
  useGetCommunity,
  useSubscribeToCommunity,
  useUnsubscribeFromCommunity,
} from '../../../redux/api/communitiesApi';
import { useGetAllCommunityPosts } from '../../../redux/api/postsApi';
import { AvatarSize } from '../../../shared/constants';
import {
  countPendingCommunityRequests,
  mapCommunitySubscriberRelationsToUsers,
} from '../../../utils/collections/map-community-subscribers';

const desktopRelationsItemsCount = 5;
const mobileRelationAvatarSize = AvatarSize.s;
const mobileRelationItemPaddingX = 8;
const mobileRelationItemGap = 4;

export function useCommunityPage(communityId: string) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const {
    data: reviewedCommunity,
    isFetching,
    error: communityError,
    refetch: refetchCommunity,
  } = useGetCommunity(communityId);
  const [subscribeToCommunity, { isLoading: isSubscribeLoading }] =
    useSubscribeToCommunity();
  const [unsubscribeFromCommunity, { isLoading: isUnsubscribeLoading }] =
    useUnsubscribeFromCommunity();
  const resolvedCommunityId = reviewedCommunity?.id;
  const canSeeDetails = !!reviewedCommunity?.permissions?.canSeeDetails;
  const canSeePosts = !!reviewedCommunity?.permissions?.canSeePosts;
  const shouldLoadCommunityPosts =
    !!resolvedCommunityId && canSeeDetails && canSeePosts;
  const {
    data: postsData,
    refetch: refetchPosts,
    isFetching: isFetchingPosts,
    error: postsError,
  } = useGetAllCommunityPosts(
    {
      communityId: resolvedCommunityId ?? '',
    },
    { skip: !shouldLoadCommunityPosts },
  );
  const posts = postsData ?? [];
  const pageDataOwners = useMemo(
    () => ({
      community: reviewedCommunity ?? null,
      posts: shouldLoadCommunityPosts ? (postsData ?? null) : null,
    }),
    [postsData, reviewedCommunity, shouldLoadCommunityPosts],
  );
  const subscribers = useMemo(
    () =>
      mapCommunitySubscriberRelationsToUsers(reviewedCommunity?.subscribers),
    [reviewedCommunity?.subscribers],
  );
  const pendingRequestsCount = countPendingCommunityRequests(
    reviewedCommunity?.requests,
  );
  useNotFound(!isFetching && !reviewedCommunity);

  async function onSubscribe() {
    if (!resolvedCommunityId) {
      return;
    }

    try {
      await subscribeToCommunity({ id: resolvedCommunityId }).unwrap();
    } catch (e) {
      console.log(e);
    }
  }

  async function onUnsubscribe() {
    if (!resolvedCommunityId) {
      return;
    }

    try {
      await unsubscribeFromCommunity({ id: resolvedCommunityId }).unwrap();
    } catch (e) {
      console.log(e);
    }
  }

  const isRelationPanel =
    !!canSeePosts && canSeeDetails && !!subscribers?.length;
  const isCommunityLoading = isFetching && !reviewedCommunity;
  const isContainerLoading =
    isCommunityLoading ||
    (shouldLoadCommunityPosts && posts.length === 0 && isFetchingPosts);
  const containerHasData =
    Boolean(pageDataOwners.community) &&
    (!shouldLoadCommunityPosts || posts.length > 0 || !postsError);
  const containerErrors = useMemo(() => {
    if (communityError && !pageDataOwners.community) {
      return [communityError];
    }

    if (
      shouldLoadCommunityPosts &&
      postsError &&
      !isFetchingPosts &&
      !pageDataOwners.posts
    ) {
      return [postsError];
    }

    return [];
  }, [
    communityError,
    isFetchingPosts,
    pageDataOwners.community,
    pageDataOwners.posts,
    postsError,
    shouldLoadCommunityPosts,
  ]);
  const onContainerErrorRetry = useCallback(() => {
    void refetchCommunity();
    void refetchPosts();
  }, [refetchCommunity, refetchPosts]);
  const pageContainerErrorState: IPageContainerRouteErrorState = useMemo(
    () => ({
      errors: containerErrors,
      isLoading: isContainerLoading,
      hasData: containerHasData,
      onRetry: onContainerErrorRetry,
    }),
    [
      containerErrors,
      isContainerLoading,
      containerHasData,
      onContainerErrorRetry,
    ],
  );
  const showNoDetailsAccess = !isFetching && !canSeeDetails;
  const showNoResult =
    shouldLoadCommunityPosts && !isFetchingPosts && posts.length === 0;
  const showNoPostsAccess = !isFetching && !canSeePosts;
  const isSubscriptionRequestSent =
    !!reviewedCommunity?.permissions?.canCancelRequestSubscription;
  const showPrivateSubscribeAction =
    !!reviewedCommunity?.permissions?.canSubscribe ||
    !!reviewedCommunity?.permissions?.canRequestSubscription;
  const privatePostsSubscribeLabelKey =
    reviewedCommunity?.permissions?.canRequestSubscription &&
    !reviewedCommunity?.permissions?.canSubscribe
      ? 'common.requestSubscription'
      : 'common.subscribe';
  const noAccessTitleKey = isSubscriptionRequestSent
    ? 'communities.privateCommunityPendingTitle'
    : 'communities.privateCommunityRequestTitle';
  const noAccessDescriptionKey = isSubscriptionRequestSent
    ? 'communities.privateCommunityPendingDescription'
    : 'communities.privateCommunityRequestDescription';
  const visibleRelationsCount = useMemo(() => {
    if (isMdUp) {
      return desktopRelationsItemsCount;
    }

    const viewportWidth =
      typeof window !== 'undefined' ? window.innerWidth : undefined;
    const mobileRelationItemWidth =
      mobileRelationAvatarSize + mobileRelationItemPaddingX;
    const mobileVisibleRelationsCount = Math.floor(
      ((viewportWidth ?? mobileRelationItemWidth) + mobileRelationItemGap) /
        (mobileRelationItemWidth + mobileRelationItemGap),
    );

    return Math.max(1, mobileVisibleRelationsCount);
  }, [isMdUp]);

  return {
    subscribers,
    reviewedCommunity,
    posts,
    isRelationPanel,
    isFetching,
    isCommunityLoading,
    showNoDetailsAccess,
    showNoResult,
    showNoPostsAccess,
    isSubscriptionRequestSent,
    showPrivateSubscribeAction,
    privatePostsSubscribeLabelKey,
    noAccessTitleKey,
    noAccessDescriptionKey,
    visibleRelationsCount,
    onSubscribe,
    onUnsubscribe,
    isSubscribeLoading,
    isUnsubscribeLoading,
    refetchAll: refetchPosts,
    isFetchingPosts,
    postsError,
    pendingRequestsCount,
    pageContainerErrorState,
  };
}
